import { Pie, Show } from "components";

import { Offer_findSingleOffer } from "_graphql/queries/__generated__/Offer";
import { classNames } from "utils";
import numeral from "numeral";
import { useState } from "react";

const pieColors = [
  "#2563EB", // Blue
  "#0d9488", // Teal
  "#9333ea", // Purple
  "#CA8A04", // Yellow
  "#EF4444", // Red
];

const pieBgColors = [
  "bg-blue-600",
  "bg-teal-600",
  "bg-purple-600",
  "bg-yellow-600",
  "bg-red-600",
];

type Props = {
  offer: Offer_findSingleOffer | null | undefined;
};

const ParticipationOverview = ({ offer }: Props) => {
  const participants = [...(offer?.offer_participant || [])];

  return (
    <div className="bg-card-500 rounded-xl shadow-lg overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-700/50">
        <h3 className="text-xl font-semibold text-primary">
          Offer Participation Overview
        </h3>
        <p className="mt-1 text-sm text-secondary">
          View and manage offer participants and participation details
        </p>
      </div>
      <div className="p-6">
        <Show if={participants?.length > 0}>
          <div className="bg-card-600/50 backdrop-blur-sm rounded-xl shadow-lg p-6">
            <div className="flex space-x-8">
              <div className="flex-1 space-y-4">
                {participants
                  ?.map((participant, index) => ({
                    color: pieColors.at(index % pieColors.length) || "#2563EB",
                    bgColor:
                      pieBgColors.at(index % pieBgColors.length) ||
                      "bg-blue-600",
                    id: participant?.offer_participant_id,
                    symbol: participant?.reinsurer?.re_abbrv || "N/A",
                    label: participant?.reinsurer?.re_company_name || "Unknown",
                    value:
                      numeral(
                        participant?.offer_participant_percentage || 0
                      ).value() || 0,
                  }))
                  ?.map?.((item: any) => (
                    <div
                      key={item?._id}
                      className="bg-card-700/50 rounded-lg p-2 flex items-center space-x-4 hover:bg-card-700/70 transition-colors duration-200"
                    >
                      <div
                        className={classNames(
                          item?.bgColor,
                          "rounded-lg p-3 w-1/6 text-center shadow-lg"
                        )}
                      >
                        <span className="text-md text-white font-medium">
                          {item?.symbol}
                        </span>
                      </div>
                      <div className="flex-1">
                        <dt className="text-xs font-medium text-secondary">
                          {item?.label}
                        </dt>
                        <dd className="mt-1">
                          <p className="text-lg font-semibold text-tertiary">
                            {numeral(item?.value).format("0,0.00")}
                          </p>
                        </dd>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="w-80 h-80">
                <Pie
                  data={
                    participants?.map((participant, index) => ({
                      color:
                        pieColors.at(index % pieColors.length) || "#2563EB",
                      bgColor:
                        pieBgColors.at(index % pieBgColors.length) ||
                        "bg-blue-600",
                      id: participant?.offer_participant_id || "",
                      symbol: participant?.reinsurer?.re_abbrv,
                      label:
                        participant?.reinsurer?.re_company_name || "Unknown",
                      value:
                        numeral(
                          participant?.offer_participant_percentage || 0
                        ).value() || 0,
                    })) || []
                  }
                />
              </div>
            </div>
          </div>
        </Show>
        <Show if={participants?.length === 0}>
          <div className="bg-card-500 rounded-xl shadow-lg backdrop-blur-sm  p-6 items-center justify-center flex flex-col space-y-4">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-700">
              <svg
                className="h-6 w-6 text-gray-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6M4 20h5M6 8a4 4 0 118 0 4 4 0 01-8 0z"
                />
              </svg>
            </div>
            <p className="text-sm text-secondary">
              No participants found for this offer.
            </p>
          </div>
        </Show>
      </div>
    </div>
  );
};

export default ParticipationOverview;
